/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * i18next setup. Locale bundles are auto-registered from ./locales/*.json — drop a new
 * `<code>.json` file in that folder (matching a backend language code) and it becomes selectable.
 * Missing keys fall back to English, so partial translations are safe.
 */
const localeModules = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*.json', {
  eager: true,
});

const resources: Record<string, { translation: Record<string, unknown> }> = {};
for (const [path, mod] of Object.entries(localeModules)) {
  // Path looks like "./locales/fr-CA.json" — take the basename without the ".json" suffix.
  const code = path.slice(path.lastIndexOf('/') + 1, -'.json'.length);
  resources[code] = { translation: mod.default };
}

/** Language codes that ship with a translation bundle (others fall back to English). */
export const SUPPORTED_LANGUAGES = Object.keys(resources);

void i18n.use(initReactI18next).init({
  resources,
  lng: 'en',
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
});

// Keep the document language in sync for accessibility (screen readers, spell-check, etc.).
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

export default i18n;
